import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowUpRight } from "lucide-react";
import { SiteHeader, SiteFooter, PageHero } from "./SiteLayout";
import { useSiteContent, usePageMeta } from "./siteContent";

const Q = [
  { key: "a", label: "A — Nature of Event", required: true, options: [
    ["Classical / seated performance", 1], ["Agricultural / country show", 2], ["Carnival", 2], ["Sport — low risk", 2],
    ["Marine / aviation / motorcycle display", 3], ["Bonfire / fireworks display", 3], ["New Year celebrations", 3],
    ["Pop / rock concert", 4], ["Music festival", 4], ["Sport — high risk (boxing, motorsport)", 4], ["Dance event / nightclub", 5]] },
  { key: "a2", label: "A.2 — Demonstrations / Marches / Political events", optional: true, options: [
    ["Not applicable", 0], ["Low risk of disorder", 2], ["Medium risk of disorder", 3], ["High risk of disorder", 4]] },
  { key: "b", label: "B — Venue", required: true, options: [
    ["Indoor", 1], ["Outdoor — confined", 2], ["Outdoor — widespread", 3], ["Temporary structures", 4], ["Multiple sites", 4]] },
  { key: "c", label: "C — Standing / Seated", required: true, options: [["Seated", 1], ["Mixed", 2], ["Standing", 3]] },
  { key: "d", label: "D — Audience Profile", required: true, options: [
    ["Full mix, good natured", 1], ["Predominantly young", 2], ["Predominantly elderly", 2], ["Rival factions / potential conflict", 4]] },
  { key: "e", label: "E — Past History", options: [["Good, no issues", 1], ["Minor issues previously", 2], ["Major issues previously", 3]] },
  { key: "f", label: "F — Expected Numbers", required: true, options: [
    ["Under 1,000", 1], ["1,000 – 3,000", 2], ["3,000 – 5,000", 3], ["5,000 – 10,000", 4], ["10,000 – 20,000", 5], ["Over 20,000", 6]] },
  { key: "g", label: "G — Expected Queuing", options: [["None", 1], ["Moderate", 2], ["Heavy / prolonged", 3]] },
  { key: "h", label: "H — Time of Year", required: true, options: [["Winter", 1], ["Spring / Autumn", 2], ["Summer (heat)", 3]] },
  { key: "i", label: "I — Proximity to Nearest A&E", options: [["Under 15 minutes", 1], ["15 – 45 minutes", 2], ["Over 45 minutes", 3]] },
  { key: "j", label: "J — Profile of Definitive Care", options: [["Full hospital with A&E", 1], ["Limited services / minor injuries", 2], ["Remote / very limited", 3]] },
];

const K_ITEMS = ["Carnival", "Parachute Display", "Helicopters", "Street Theatre", "Motorsport"];
const L_ITEMS = ["Suturing", "Plastering", "X-Ray", "Psychiatric / GP Facilities", "Minor Surgery"];

const CONVERSION = [
  ["< 20", "4", "0 / 0", "0", "0", "0", "0"],
  ["21–25", "6", "1 / 2", "0", "0", "VISIT", "0"],
  ["26–30", "8", "1 / 2", "0", "0", "VISIT", "0"],
  ["31–35", "12", "2 / 8", "1", "2", "1", "0"],
  ["36–40", "20", "3 / 10", "2", "4", "1", "0"],
  ["41–50", "40", "4 / 12", "3", "6", "2", "1"],
  ["51–60", "60", "4 / 12", "4", "8", "2", "1"],
  ["61–65", "80", "5 / 14", "5", "10", "3", "1"],
  ["66–70", "100", "6 / 16", "6", "12", "4", "2"],
  ["71–75", "150", "10 / 24", "9", "18", "6", "3"],
  ["> 75", "200+", "15+ / 35+", "12+", "24+", "8+", "3"],
];

const TIERS = [
  ["Tier 1 — Low risk", "< 500 attendees", "Minimum of 1 to 2 qualified first aiders or first responders."],
  ["Tier 2 — Low risk", "500 – 2,000 attendees", "Essential minimum of 4 first responders, with a desirable addition of 1 paramedic or nurse and a site ambulance."],
  ["Tier 3", "2,001 – 5,000 attendees", "Essential team of 1 paramedic, 2 nurses, 6 responders, and 1 crewed ambulance."],
  ["Tier 3", "5,001 – 10,000 attendees", "Essential team of 2 paramedics, 2 nurses, 8 responders, and 1 crewed ambulance."],
  ["Tier 4", "10,000 – 20,000 attendees", "Essential team of 1 doctor, 2 paramedics, 4 nurses, 8 responders, and 1 crewed ambulance."],
];

function recommendation(score) {
  for (const row of CONVERSION) {
    const band = row[0];
    if (band.startsWith("<")) { if (score < parseInt(band.slice(1), 10)) return row; }
    else if (band.startsWith(">")) { if (score > parseInt(band.slice(1), 10)) return row; }
    else { const [lo, hi] = band.split("–").map((x) => parseInt(x, 10)); if (score >= lo && score <= hi) return row; }
  }
  return CONVERSION[0];
}

const selectCls = "w-full h-12 rounded-xl border border-slate-700 bg-[#1A232E] text-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#00D2C2]";

export default function SiteCalculator() {
  const site = useSiteContent();
  usePageMeta("Calculate What Medical Staff You Need For Your Event | Wales Event Medical Services",
    "Free Purple/Green Guide tool + Martyn's Law compliance — work out the medical cover your event needs in minutes with our staffing calculator.", "", "calculator");
  const ct = site.calculator_texts || {};
  const [answers, setAnswers] = useState({});
  const [k, setK] = useState([]);
  const [l, setL] = useState([]);
  const [result, setResult] = useState(null);

  const score = useMemo(() => {
    let s = 0;
    Object.values(answers).forEach((v) => { s += Number(v) || 0; });
    s += 2 * k.length;
    s -= 2 * l.length;
    return Math.max(s, 0);
  }, [answers, k, l]);

  const toggle = (list, setList, item) =>
    setList((cur) => (cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item]));

  return (
    <div data-testid="site-calculator">
      <SiteHeader />
      <PageHero title={ct.hero_title} subtitle={ct.hero_subtitle} image={ct.hero_image} />

      <section className="py-20 bg-[#0B0F14]">
        <div className="container-x max-w-3xl">
          <div className="card-soft p-8 md:p-10">
            <div className="flex items-center gap-3">
              <span className="inline-flex w-12 h-12 rounded-xl bg-[#00D2C2]/15 text-[#00D2C2] items-center justify-center"><Calculator size={24} /></span>
              <h2 className="font-serif-site text-2xl font-semibold text-white">{ct.intro_title}</h2>
            </div>
            <p className="mt-4 text-sm text-slate-300 leading-relaxed">{ct.intro_text}</p>
            <p className="mt-3 text-xs text-slate-400 leading-relaxed">
              {ct.disclaimer} <Link to="/contact-us" className="text-[#00D2C2] font-semibold hover:underline">Contact our team</Link> for a formal risk assessment.
            </p>

            <div className="mt-8 space-y-5">
              {Q.map((q) => (
                <label key={q.key} className="block">
                  <span className="text-xs font-semibold text-slate-300 block mb-1.5">
                    {q.label} {q.required ? "*" : <span className="text-slate-400 font-normal">{q.optional ? "(optional — only if applicable)" : ""}</span>}
                  </span>
                  <select className={selectCls} data-testid={`calc-${q.key}`} value={answers[q.key] ?? ""}
                    onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}>
                    <option value="">Select an option…</option>
                    {q.options.map(([label, v]) => <option key={label} value={v}>{label}</option>)}
                  </select>
                </label>
              ))}
              <div>
                <span className="text-xs font-semibold text-slate-300 block mb-1.5">K — Additional Hazards (choose all that apply)</span>
                <div className="flex flex-wrap gap-2">
                  {K_ITEMS.map((item) => (
                    <button type="button" key={item} onClick={() => toggle(k, setK, item)} data-testid={`calc-k-${item.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`px-3.5 py-2 rounded-full text-xs font-bold border transition-colors ${k.includes(item) ? "bg-[#1e7d8c] border-[#1e7d8c] text-white" : "border-[#E2E8F0] text-slate-300 hover:border-[#1e7d8c]"}`}>
                      {item}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-300 block mb-1.5">L — Additional On-Site Facilities (these reduce your score)</span>
                <div className="flex flex-wrap gap-2">
                  {L_ITEMS.map((item) => (
                    <button type="button" key={item} onClick={() => toggle(l, setL, item)} data-testid={`calc-l-${item.toLowerCase().replace(/[\s/]+/g, "-")}`}
                      className={`px-3.5 py-2 rounded-full text-xs font-bold border transition-colors ${l.includes(item) ? "bg-[#0F172A] border-[#0F172A] text-white" : "border-[#E2E8F0] text-slate-300 hover:border-[#0F172A]"}`}>
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
              <p className="text-sm font-bold text-white" data-testid="calc-live-score">
                Live risk score: <span className="text-[#00D2C2] text-xl font-serif-site">{score}</span>
              </p>
              <button type="button" onClick={() => setResult(recommendation(score))} className="btn btn-primary" data-testid="calc-submit">
                Calculate Staffing
              </button>
            </div>

            {result && (
              <div className="mt-6 rounded-2xl bg-[#0F172A] text-white p-6" data-testid="calc-result">
                <p className="text-xs font-bold uppercase tracking-widest text-[#F0B486]">Recommended provision (score band {result[0]})</p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                  <div><p className="text-slate-400 text-xs">First Responders</p><p className="font-bold text-lg">{result[1]}</p></div>
                  <div><p className="text-slate-400 text-xs">Ambulances / Personnel</p><p className="font-bold text-lg">{result[2]}</p></div>
                  <div><p className="text-slate-400 text-xs">Doctors</p><p className="font-bold text-lg">{result[3]}</p></div>
                  <div><p className="text-slate-400 text-xs">Nurses</p><p className="font-bold text-lg">{result[4]}</p></div>
                  <div><p className="text-slate-400 text-xs">Ambulance Manager</p><p className="font-bold text-lg">{result[5]}</p></div>
                  <div><p className="text-slate-400 text-xs">Support Unit</p><p className="font-bold text-lg">{result[6]}</p></div>
                </div>
                <Link to="/intake" className="btn btn-primary mt-6" data-testid="calc-result-quote">
                  Get a quote for this cover <ArrowUpRight size={16} />
                </Link>
              </div>
            )}
          </div>

          <div className="mt-14">
            <h3 className="font-serif-site text-2xl font-semibold text-white">{ct.tiers_title}</h3>
            <p className="mt-2 text-sm text-slate-300">{ct.tiers_text}</p>
            <div className="mt-5 overflow-x-auto card-soft">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#0F172A] text-white text-left">
                  <th className="px-4 py-3 font-semibold">Tier</th><th className="px-4 py-3 font-semibold">Attendance</th><th className="px-4 py-3 font-semibold">Recommended staffing</th>
                </tr></thead>
                <tbody>
                  {TIERS.map((t, i) => (
                    <tr key={i} className={i % 2 ? "bg-[#1A232E]" : "bg-[#121820]"}>
                      <td className="px-4 py-3 font-semibold text-white whitespace-nowrap">{t[0]}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{t[1]}</td>
                      <td className="px-4 py-3 text-slate-300">{t[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-12">
            <h3 className="font-serif-site text-2xl font-semibold text-white">{ct.table_title}</h3>
            <div className="mt-5 overflow-x-auto card-soft">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#0F172A] text-white text-left">
                  {["Score", "First Responder", "Ambulances / Personnel", "Doctor", "Nurse", "Ambulance Manager", "Support Unit"].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {CONVERSION.map((r, i) => (
                    <tr key={r[0]} className={i % 2 ? "bg-[#1A232E]" : "bg-[#121820]"}>
                      {r.map((c, j) => <td key={j} className={`px-4 py-2.5 whitespace-nowrap ${j === 0 ? "font-bold text-white" : "text-slate-300"}`}>{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-slate-400 leading-relaxed">
              Based on the Health & Safety Executive's Purple Guide scoring system. This is a guide only — the ultimate
              responsibility for adequate and competent medical provision rests with the event organiser.
            </p>
          </div>

          <div className="mt-12">
            <h3 className="font-serif-site text-2xl font-semibold text-white">{ct.martyns_title}</h3>
            <div className="mt-5 grid md:grid-cols-2 gap-5">
              <div className="card-soft p-6">
                <p className="text-xs font-bold uppercase tracking-widest text-[#00D2C2]">Standard Tier</p>
                <p className="font-bold text-white mt-1">200 – 799 capacity</p>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                  Venues focus on procedural readiness rather than heavy physical security. Staff must know how to
                  evacuate, lock down, report suspicious behaviour, and communicate during an emergency.
                </p>
              </div>
              <div className="card-soft p-6">
                <p className="text-xs font-bold uppercase tracking-widest text-[#00D2C2]">Enhanced Tier</p>
                <p className="font-bold text-white mt-1">800+ capacity</p>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                  Venues must implement active measures like CCTV, bag searches, risk assessments, and designated
                  compliance oversight, alongside comprehensive staff training.
                </p>
              </div>
            </div>
            <div className="card-soft p-6 mt-5">
              <p className="text-xs font-bold uppercase tracking-widest text-[#00D2C2]">Medical & staffing guidance</p>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                While the core legislation emphasises security, emergency medical preparedness and staff capability are
                integrated into compliance and sector-specific guidance (such as for healthcare settings and large public events):
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-300 list-disc pl-5 leading-relaxed">
                <li><strong>Role-Appropriate Training:</strong> Training must match the specific responsibilities of the staff member and align with the operating environment rather than just providing generic awareness.</li>
                <li><strong>Emergency Response & First Aid:</strong> Guidance recommends embedding immediate life-saving skills — such as critical bleeding control, managing major trauma, and utilising essential emergency medical supplies — into staff preparedness plans, emphasising the critical early response (the "platinum ten minutes") following an incident.</li>
                <li><strong>Staffing Levels:</strong> Organisations are expected to maintain adequate staffing with appropriate training on-site during operational hours or events to ensure emergency plans, evacuations, and initial medical/security responses can be executed swiftly.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
